import { DateTime } from 'luxon';

import { ListTemplate, Organization } from '../../types';
import { parseDate } from '../../utils/DateTime';
import { OrganizationComponent, OrganizationTemplateType } from '../components/organization.template';
import { DocumentTypePopulator } from '../DocumentGenerator';
import { applyListMeta, formatCurrency } from './formatters';

export type BalanceSheetReportInputType = {
  organization: Organization;
  balanceSheetAccounts: Array<{
    path: string;
    balance: string;
    ownBalance: string;
    previousPeriodBalance?: string;
    previousPeriodOwnBalance?: string;
    previousYearBalance?: string;
    previousYearOwnBalance?: string;
  }>;
  ledgerAccounts: Array<{
    id: number;
    accountName: string;
    accountNumber: string;
    accountType: string;
    fragmentAccountPath: string;
    path: string;
    name: string;
    number: string;
  }>;
  reportPeriod: { start: string; end: string };
  compareTo: { previousPeriod?: boolean; previousYear?: boolean };
  hideZeroBalance?: boolean;
};

export type FlattenedAccount = {
  id: string;
  accountNumber: string;
  accountName: string;
  accountType: string;
  balance: string;
  balanceRaw: string;
  balanceFmt: string;
  previousPeriodBalance?: string;
  previousYearBalance?: string;
  depth: number;
  isGroupHeader: boolean;
  isSubtotal: boolean;
  parentGroup?: string;
  hasChildren: boolean;
  isLastChild: boolean;
  isZeroBalance: boolean;
};

export type BalanceSheetAccountGroup = {
  accountType: string;
  balance: string;
  balanceRaw: string;
  balanceFmt: string;
  previousPeriodBalance?: string;
  previousYearBalance?: string;
  accounts: ListTemplate<FlattenedAccount>;
};

export type BalanceSheetReportData = {
  organization: OrganizationTemplateType;
  accrualBasis: string;
  reportDate: string;
  reportPeriod: string;
  hasComparisons: boolean;
  hasPreviousPeriod: boolean;
  hasPreviousYear: boolean;

  // Assets
  currentAssets: BalanceSheetAccountGroup;
  longTermAssets: BalanceSheetAccountGroup;
  otherAssets: BalanceSheetAccountGroup;
  totalAssets: string;
  totalAssetsPreviousPeriod?: string;
  totalAssetsPreviousYear?: string;

  // Liabilities
  currentLiabilities: BalanceSheetAccountGroup;
  longTermLiabilities: BalanceSheetAccountGroup;
  totalLiabilities: string;
  totalLiabilitiesPreviousPeriod?: string;
  totalLiabilitiesPreviousYear?: string;

  // Equity
  equity: BalanceSheetAccountGroup;
  netIncome: string;
  netIncomePreviousPeriod?: string;
  netIncomePreviousYear?: string;
  totalEquity: string;
  totalEquityPreviousPeriod?: string;
  totalEquityPreviousYear?: string;

  // Totals
  totalLiabilitiesAndEquity: string;
  totalLiabilitiesAndEquityPreviousPeriod?: string;
  totalLiabilitiesAndEquityPreviousYear?: string;
};

// Type for account with tree structure
type AccountWithChildren = {
  accountPath: string;
  path?: string;
  fragmentAccountPath?: string;
  children: AccountWithChildren[];
  pathSegments: string[];
  depth: number;
  [key: string]: any;
};

// Helper function to convert fragment response to tree hierarchy
const convertFragmentResponseToTreeHierarchy = (accounts: any[]): AccountWithChildren[] => {
  if (!accounts || accounts.length === 0) return [];

  // Sort accounts by path depth and then by path for consistent ordering
  const sortedAccounts = [...accounts].sort((a, b) => {
    const pathA = a.accountPath || '';
    const pathB = b.accountPath || '';

    const depthA = pathA.split('/').length;
    const depthB = pathB.split('/').length;

    // First sort by depth, then by path
    if (depthA !== depthB) {
      return depthA - depthB;
    }
    return pathA.localeCompare(pathB);
  });

  const accountMap = new Map<string, AccountWithChildren>();
  const rootAccounts: AccountWithChildren[] = [];

  // Process accounts in order, building the tree structure
  sortedAccounts.forEach((account) => {
    const accountPath = account.accountPath || account.path || account.fragmentAccountPath || '';

    const pathSegments = accountPath.split('/').filter((segment: string) => segment.length > 0);

    const accountWithChildren: AccountWithChildren = {
      ...account,
      children: [],
      accountPath: accountPath,
      pathSegments: pathSegments,
      depth: pathSegments.length - 1, // 0-based depth
    };

    accountMap.set(accountPath, accountWithChildren);

    if (pathSegments.length === 1) {
      // Root level account
      rootAccounts.push(accountWithChildren);
    } else {
      // Find parent by removing last path segment
      const parentPath = pathSegments.slice(0, -1).join('/');
      const parent = accountMap.get(parentPath);

      if (parent) {
        parent.children.push(accountWithChildren);
      } else {
        // Parent not found, treat as root (shouldn't happen with proper sorting)
        rootAccounts.push(accountWithChildren);
      }
    }
  });

  return rootAccounts;
};

const parseCents = (cents: string) => {
  const num = parseInt(cents, 10);
  return isNaN(num) ? 0 : num / 100;
};

// Helper function to flatten a single account group
// drop-in replacement: simplified + no dupes + correct TOTAL placement
const flattenAccountGroup = (
  accounts: any[],
  accountType: string,
  showAccountNumbers = false,
  hideZeroBalances = false,
  allAccountsForReference: any[] = []
): FlattenedAccount[] => {
  const flattened: FlattenedAccount[] = [];

  // ---- tiny helpers ----
  const getPath = (x: any) => x.accountPath || x.path || x.fragmentAccountPath || '';
  const segs = (p: string) => p.split('/').filter(Boolean);
  const depthOf = (x: any) => segs(getPath(x)).length - 1;
  const lastSeg = (x: any) => {
    const s = segs(getPath(x));
    return s[s.length - 1] || '';
  };
  const acctNum = (x: any): number | null => {
    const raw = (x.accountNumber ?? '').toString();
    const m = raw.match(/\d+/);
    return m ? Number(m[0]) : null;
  };
  const cmpSiblings = (a: any, b: any) => {
    // same depth (siblings); guard anyway
    const d = depthOf(a) - depthOf(b);
    if (d !== 0) return d;

    const nA = acctNum(a),
      nB = acctNum(b);
    if (nA !== null && nB === null) return -1;
    if (nA === null && nB !== null) return 1;
    if (nA !== null && nB !== null && nA !== nB) return nA - nB;

    const byName = lastSeg(a).localeCompare(lastSeg(b), undefined, { numeric: true, sensitivity: 'base' });
    if (byName !== 0) return byName;

    return getPath(a).localeCompare(getPath(b), undefined, { numeric: true, sensitivity: 'base' });
  };

  // derive children only if a node has none attached
  const ref = allAccountsForReference.length ? allAccountsForReference : accounts;
  const getImmediateChildren = (parent: any): any[] => {
    const attached = Array.isArray(parent.children) ? parent.children : [];
    if (attached.length) return attached;

    const p = getPath(parent);
    if (!p) return [];
    const parentDepth = segs(p).length;
    const out: any[] = [];
    for (const a of ref) {
      const ap = getPath(a);
      if (!ap || ap === p) continue;
      if (!ap.startsWith(p + '/')) continue;
      if (segs(ap).length === parentDepth + 1) out.push(a);
    }
    return out;
  };

  // pick true roots from whatever we received (tree or flat)
  const input = Array.isArray(accounts) ? accounts.slice() : [];
  const pathSet = new Set(input.map(getPath).filter(Boolean));
  const roots = input.filter((a) => {
    const p = getPath(a);
    const s = segs(p);
    if (s.length <= 1) return true;
    const parentPath = s.slice(0, -1).join('/');
    return !pathSet.has(parentPath);
  });

  // prevent visiting same node twice (handles mixed tree/flat input)
  const visited = new Set<string>();
  const keyOf = (x: any) => {
    const p = getPath(x);
    if (p) return 'path:' + p;
    if (x.id) return 'id:' + x.id;
    if (x.accountNumber) return 'num:' + x.accountNumber;
    return 'name:' + (x.accountName || x.name || '');
  };

  const emitRow = (
    account: any,
    depth: number,
    parentId: string | undefined,
    isLastSibling: boolean,
    isSubtotal = false,
    balanceStr?: string | number
  ) => {
    const p = getPath(account);
    const id =
      (account.id || account.accountNumber || `${accountType}_${account.accountName || account.name}_${p}`) +
      (isSubtotal ? '_total' : '');
    const hasChildren = !isSubtotal && getImmediateChildren(account).length > 0;

    const choose = isSubtotal ? (balanceStr ?? account.balance) : hasChildren ? account.ownBalance : account.balance;
    const amount = parseCents((choose ?? '0').toString()) || 0;

    const balanceRaw = amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const balanceFmt = formatCurrency(amount, 'USD');
    const balanceOut = isSubtotal
      ? balanceFmt
      : amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const isZero = amount === 0;

    // For parent accounts, recursively check if any descendant has non-zero balances
    const hasNonZeroDescendants = (node: any): boolean => {
      const children = getImmediateChildren(node);
      return children.some((child) => {
        const childBalance = parseCents((child.balance ?? '0').toString()) || 0;
        return childBalance !== 0 || hasNonZeroDescendants(child);
      });
    };

    const shouldShow = !hideZeroBalances || !isZero || (hasChildren && hasNonZeroDescendants(account));

    if (!shouldShow) return;

    const display =
      showAccountNumbers && account.accountNumber
        ? `${account.accountNumber} - ${account.accountName || ''}`
        : account.accountName || '';

    flattened.push({
      id,
      accountNumber: isSubtotal ? '' : account.accountNumber,
      accountName: isSubtotal ? `Total ${account.accountName || ''}` : display,
      accountType,
      balance: balanceOut,
      balanceRaw,
      balanceFmt,
      previousPeriodBalance: undefined,
      previousYearBalance: undefined,
      depth,
      isGroupHeader: false,
      isSubtotal,
      parentGroup: parentId,
      hasChildren: isSubtotal ? false : hasChildren,
      isLastChild: isLastSibling,
      isZeroBalance: isZero,
    });
  };

  const walk = (node: any, parentId?: string, isLastSibling = false) => {
    const k = keyOf(node);
    if (visited.has(k)) return;
    visited.add(k);

    const depth = depthOf(node);
    const kids = getImmediateChildren(node).slice().sort(cmpSiblings);

    // parent row (uses ownBalance if it has children)
    emitRow(node, depth, parentId, isLastSibling, false);

    // children
    kids.forEach((child, i) => walk(child, node.id || node.accountNumber || getPath(node), i === kids.length - 1));

    // subtotal row AFTER children
    if (kids.length) emitRow(node, depth, parentId, isLastSibling, true, node.balance);
  };

  roots.sort(cmpSiblings).forEach((r, i) => walk(r, undefined, i === roots.length - 1));
  return flattened;
};

export const BalanceSheetReportTemplate: DocumentTypePopulator<
  BalanceSheetReportInputType,
  void,
  BalanceSheetReportData
> = {
  populate: (data, _, globalOptions) => {
    if (!data) return {} as BalanceSheetReportData;

    const { organization, balanceSheetAccounts, ledgerAccounts, reportPeriod, compareTo, hideZeroBalance } = data;

    // Merge settings from global options if available
    const settings = globalOptions?._settings || {};
    const showAccountNumbers = settings.showAccountNumbers ?? globalOptions?.showAccountNumbers ?? false;
    const hideZeroBalanceSetting =
      settings.hideZeroBalance ?? globalOptions?.hideZeroBalance ?? hideZeroBalance ?? false;

    // Group accounts by type (mirroring frontend logic)
    const subgroupAccounts: Record<string, any> = {
      CurrentAssets: [],
      LongTermAssets: [],
      OtherAssets: [],
      CurrentLiabilities: [],
      LongTermLiabilities: [],
      Equity: [],
      DirectCosts: [],
      Income: [],
      OtherIncome: [],
      OtherCosts: [],
      IndirectCosts: [],
    };

    // Process each balance sheet account
    balanceSheetAccounts.forEach((account: any) => {
      const accountDetails = ledgerAccounts.find((accountDetail) => accountDetail.fragmentAccountPath === account.path);

      if (!accountDetails) {
        console.warn('No ledger account detail found for', account.path);
        return;
      }

      if (hideZeroBalanceSetting && (!account.balance || parseInt(account.balance) === 0)) {
        return;
      }

      let accountType = accountDetails.accountType;

      // Handle CashAssets -> CurrentAssets mapping
      if (accountType === 'CashAssets') {
        accountType = 'CurrentAssets';
      }

      const acc = {
        ...account,
        accountType: accountType,
        accountName: accountDetails.accountName,
        accountNumber: accountDetails.accountNumber,
        accountPath: accountDetails.path,
      };

      if (subgroupAccounts[accountType]) {
        subgroupAccounts[accountType].push(acc);
      }
    });

    // Calculate totals for each group and create flattened account lists
    Object.keys(subgroupAccounts).forEach((type) => {
      const accountTree = convertFragmentResponseToTreeHierarchy(subgroupAccounts[type]);

      // Only sum top-level accounts (depth 0) to avoid double-counting parent+child balances
      const balance = accountTree.reduce((tot: number, acc: any) => {
        // Only count accounts at root level (depth 0) since their balance already includes children
        const pathSegments = (acc.accountPath || acc.path || '').split('/').filter((s: string) => s.length > 0);
        const depth = pathSegments.length - 1;
        return depth === 0 ? tot + (parseCents(acc.balance) || 0) : tot;
      }, 0);

      const prevPer = accountTree.reduce((tot: number, acc: any) => {
        const pathSegments = (acc.accountPath || acc.path || '').split('/').filter((s: string) => s.length > 0);
        const depth = pathSegments.length - 1;
        return depth === 0 ? tot + (parseCents(acc.previousPeriodBalance) || 0) : tot;
      }, 0);

      const prevYear = accountTree.reduce((tot: number, acc: any) => {
        const pathSegments = (acc.accountPath || acc.path || '').split('/').filter((s: string) => s.length > 0);
        const depth = pathSegments.length - 1;
        return depth === 0 ? tot + (parseCents(acc.previousYearBalance) || 0) : tot;
      }, 0);

      // Create flattened account list - pass the original flat accounts for reference
      const originalAccounts = subgroupAccounts[type];
      const flattenedAccounts = flattenAccountGroup(
        accountTree,
        type,
        showAccountNumbers,
        hideZeroBalanceSetting,
        originalAccounts
      );

      const balanceValue = balance.toFixed(2);
      subgroupAccounts[type] = {
        accountType: type,
        balance: balanceValue,
        balanceRaw: parseFloat(balanceValue).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        balanceFmt: formatCurrency(parseFloat(balanceValue), 'USD'),
        previousPeriodBalance: prevPer.toFixed(2),
        previousYearBalance: prevYear.toFixed(2),
        accounts: applyListMeta(flattenedAccounts),
      };
    });

    // Calculate financial statement components
    const incomeAccount = subgroupAccounts['Income'];
    const cogsAccount = subgroupAccounts['DirectCosts'];
    const expenses = subgroupAccounts['IndirectCosts'];
    const otherIncome = subgroupAccounts['OtherIncome'];
    const otherExpenses = subgroupAccounts['OtherCosts'];

    const grossProfit = parseFloat(incomeAccount.balance) - parseFloat(cogsAccount.balance);
    const grossProfitPreviousPeriod =
      parseFloat(incomeAccount.previousPeriodBalance) - parseFloat(cogsAccount.previousPeriodBalance);
    const grossProfitPreviousYear =
      parseFloat(incomeAccount.previousYearBalance) - parseFloat(cogsAccount.previousYearBalance);

    const netOperatingIncome = grossProfit - parseFloat(expenses.balance);
    const netOperatingIncomePreviousPeriod = grossProfitPreviousPeriod - parseFloat(expenses.previousPeriodBalance);
    const netOperatingIncomePreviousYear = grossProfitPreviousYear - parseFloat(expenses.previousYearBalance);

    const netOtherIncome = parseFloat(otherIncome.balance) - parseFloat(otherExpenses.balance);
    const netOtherIncomePreviousPeriod =
      parseFloat(otherIncome.previousPeriodBalance) - parseFloat(otherExpenses.previousPeriodBalance);
    const netOtherIncomePreviousYear =
      parseFloat(otherIncome.previousYearBalance) - parseFloat(otherExpenses.previousYearBalance);

    const netIncome = netOperatingIncome + netOtherIncome;
    const netIncomePreviousPeriod = netOperatingIncomePreviousPeriod + netOtherIncomePreviousPeriod;
    const netIncomePreviousYear = netOperatingIncomePreviousYear + netOtherIncomePreviousYear;

    // Calculate totals
    const totalAssets =
      parseFloat(subgroupAccounts['CurrentAssets'].balance) +
      parseFloat(subgroupAccounts['LongTermAssets'].balance) +
      parseFloat(subgroupAccounts['OtherAssets'].balance);

    const totalAssetsPreviousPeriod =
      parseFloat(subgroupAccounts['CurrentAssets'].previousPeriodBalance) +
      parseFloat(subgroupAccounts['LongTermAssets'].previousPeriodBalance) +
      parseFloat(subgroupAccounts['OtherAssets'].previousPeriodBalance);

    const totalAssetsPreviousYear =
      parseFloat(subgroupAccounts['CurrentAssets'].previousYearBalance) +
      parseFloat(subgroupAccounts['LongTermAssets'].previousYearBalance) +
      parseFloat(subgroupAccounts['OtherAssets'].previousYearBalance);

    const totalLiabilities =
      parseFloat(subgroupAccounts['CurrentLiabilities'].balance) +
      parseFloat(subgroupAccounts['LongTermLiabilities'].balance);

    const totalLiabilitiesPreviousPeriod =
      parseFloat(subgroupAccounts['CurrentLiabilities'].previousPeriodBalance) +
      parseFloat(subgroupAccounts['LongTermLiabilities'].previousPeriodBalance);

    const totalLiabilitiesPreviousYear =
      parseFloat(subgroupAccounts['CurrentLiabilities'].previousYearBalance) +
      parseFloat(subgroupAccounts['LongTermLiabilities'].previousYearBalance);

    const totalEquity = parseFloat(subgroupAccounts['Equity'].balance) + netIncome;
    const totalEquityPreviousPeriod =
      parseFloat(subgroupAccounts['Equity'].previousPeriodBalance) + netIncomePreviousPeriod;
    const totalEquityPreviousYear = parseFloat(subgroupAccounts['Equity'].previousYearBalance) + netIncomePreviousYear;

    const totalLiabilitiesAndEquity = totalLiabilities + totalEquity;
    const totalLiabilitiesAndEquityPreviousPeriod = totalLiabilitiesPreviousPeriod + totalEquityPreviousPeriod;
    const totalLiabilitiesAndEquityPreviousYear = totalLiabilitiesPreviousYear + totalEquityPreviousYear;

    // Format report period
    const startDate = parseDate(reportPeriod.start);
    const endDate = parseDate(reportPeriod.end);
    const reportPeriodFormatted =
      startDate && endDate
        ? `${startDate.toFormat('MMM dd, yyyy')} - ${endDate.toFormat('MMM dd, yyyy')}`
        : 'Custom Period';

    const accrualBasis = (endDate || DateTime.now()).toLocaleString(DateTime.DATETIME_FULL);

    return {
      organization: OrganizationComponent.templatize({ organization, customOrgName: globalOptions.customOrgName }),
      accrualBasis,
      reportDate: (endDate || DateTime.now()).toFormat('MMMM dd, yyyy'),
      reportPeriod: reportPeriodFormatted,
      hasComparisons: !!(compareTo.previousPeriod || compareTo.previousYear),
      hasPreviousPeriod: !!compareTo.previousPeriod,
      hasPreviousYear: !!compareTo.previousYear,

      // Assets
      currentAssets: subgroupAccounts['CurrentAssets'],
      longTermAssets: subgroupAccounts['LongTermAssets'],
      otherAssets: subgroupAccounts['OtherAssets'],
      totalAssets: formatCurrency(totalAssets, 'USD'),
      totalAssetsPreviousPeriod: compareTo.previousPeriod
        ? formatCurrency(totalAssetsPreviousPeriod, 'USD')
        : undefined,
      totalAssetsPreviousYear: compareTo.previousYear ? formatCurrency(totalAssetsPreviousYear, 'USD') : undefined,

      // Liabilities
      currentLiabilities: subgroupAccounts['CurrentLiabilities'],
      longTermLiabilities: subgroupAccounts['LongTermLiabilities'],
      totalLiabilities: formatCurrency(totalLiabilities, 'USD'),
      totalLiabilitiesPreviousPeriod: compareTo.previousPeriod
        ? formatCurrency(totalLiabilitiesPreviousPeriod, 'USD')
        : undefined,
      totalLiabilitiesPreviousYear: compareTo.previousYear
        ? formatCurrency(totalLiabilitiesPreviousYear, 'USD')
        : undefined,

      // Equity
      equity: subgroupAccounts['Equity'],
      netIncome: formatCurrency(netIncome, 'USD'),
      netIncomePreviousPeriod: compareTo.previousPeriod ? formatCurrency(netIncomePreviousPeriod, 'USD') : undefined,
      netIncomePreviousYear: compareTo.previousYear ? formatCurrency(netIncomePreviousYear, 'USD') : undefined,
      totalEquity: formatCurrency(totalEquity, 'USD'),
      totalEquityPreviousPeriod: compareTo.previousPeriod
        ? formatCurrency(totalEquityPreviousPeriod, 'USD')
        : undefined,
      totalEquityPreviousYear: compareTo.previousYear ? formatCurrency(totalEquityPreviousYear, 'USD') : undefined,

      // Totals
      totalLiabilitiesAndEquity: formatCurrency(totalLiabilitiesAndEquity, 'USD'),
      totalLiabilitiesAndEquityPreviousPeriod: compareTo.previousPeriod
        ? formatCurrency(totalLiabilitiesAndEquityPreviousPeriod, 'USD')
        : undefined,
      totalLiabilitiesAndEquityPreviousYear: compareTo.previousYear
        ? formatCurrency(totalLiabilitiesAndEquityPreviousYear, 'USD')
        : undefined,
    };
  },
  definitionMap: {
    organization: { label: 'Organization', type: 'object', children: OrganizationComponent.definitionMap },
    accrualBasis: { label: 'Accrual Basis', type: 'string' },
    reportDate: { label: 'Report Date', type: 'string' },
    reportPeriod: { label: 'Report Period', type: 'string' },
    hasComparisons: { label: 'Has Comparisons', type: 'boolean' },
    hasPreviousPeriod: { label: 'Has Previous Period', type: 'boolean' },
    hasPreviousYear: { label: 'Has Previous Year', type: 'boolean' },

    // Assets
    currentAssets: {
      label: 'Current Assets',
      type: 'object',
      children: {
        accountType: { label: 'Account Type', type: 'string' },
        balance: { label: 'Balance', type: 'string' },
        balanceRaw: { label: 'Balance Raw', type: 'string' },
        balanceFmt: { label: 'Balance Formatted', type: 'string' },
        previousPeriodBalance: { label: 'Previous Period Balance', type: 'string' },
        previousYearBalance: { label: 'Previous Year Balance', type: 'string' },
        accounts: {
          label: 'Accounts',
          type: 'list',
          children: {
            id: { label: 'ID', type: 'string' },
            accountNumber: { label: 'Account Number', type: 'string' },
            accountName: { label: 'Account Name', type: 'string' },
            accountType: { label: 'Account Type', type: 'string' },
            balance: { label: 'Balance', type: 'string' },
            balanceRaw: { label: 'Balance Raw', type: 'string' },
            balanceFmt: { label: 'Balance Formatted', type: 'string' },
            previousPeriodBalance: { label: 'Previous Period Balance', type: 'string' },
            previousYearBalance: { label: 'Previous Year Balance', type: 'string' },
            depth: { label: 'Depth', type: 'number' },
            isGroupHeader: { label: 'Is Group Header', type: 'boolean' },
            isSubtotal: { label: 'Is Subtotal', type: 'boolean' },
            parentGroup: { label: 'Parent Group', type: 'string' },
            hasChildren: { label: 'Has Children', type: 'boolean' },
            isLastChild: { label: 'Is Last Child', type: 'boolean' },
            isZeroBalance: { label: 'Is Zero Balance', type: 'boolean' },
          },
        },
      },
    },
    longTermAssets: {
      label: 'Long Term Assets',
      type: 'object',
      children: {
        accountType: { label: 'Account Type', type: 'string' },
        balance: { label: 'Balance', type: 'string' },
        balanceRaw: { label: 'Balance Raw', type: 'string' },
        balanceFmt: { label: 'Balance Formatted', type: 'string' },
        previousPeriodBalance: { label: 'Previous Period Balance', type: 'string' },
        previousYearBalance: { label: 'Previous Year Balance', type: 'string' },
        accounts: {
          label: 'Accounts',
          type: 'list',
          children: {
            id: { label: 'ID', type: 'string' },
            accountNumber: { label: 'Account Number', type: 'string' },
            accountName: { label: 'Account Name', type: 'string' },
            accountType: { label: 'Account Type', type: 'string' },
            balance: { label: 'Balance', type: 'string' },
            balanceRaw: { label: 'Balance Raw', type: 'string' },
            balanceFmt: { label: 'Balance Formatted', type: 'string' },
            previousPeriodBalance: { label: 'Previous Period Balance', type: 'string' },
            previousYearBalance: { label: 'Previous Year Balance', type: 'string' },
            depth: { label: 'Depth', type: 'number' },
            isGroupHeader: { label: 'Is Group Header', type: 'boolean' },
            isSubtotal: { label: 'Is Subtotal', type: 'boolean' },
            parentGroup: { label: 'Parent Group', type: 'string' },
            hasChildren: { label: 'Has Children', type: 'boolean' },
            isLastChild: { label: 'Is Last Child', type: 'boolean' },
            isZeroBalance: { label: 'Is Zero Balance', type: 'boolean' },
          },
        },
      },
    },
    otherAssets: {
      label: 'Other Assets',
      type: 'object',
      children: {
        accountType: { label: 'Account Type', type: 'string' },
        balance: { label: 'Balance', type: 'string' },
        balanceRaw: { label: 'Balance Raw', type: 'string' },
        balanceFmt: { label: 'Balance Formatted', type: 'string' },
        previousPeriodBalance: { label: 'Previous Period Balance', type: 'string' },
        previousYearBalance: { label: 'Previous Year Balance', type: 'string' },
        accounts: {
          label: 'Accounts',
          type: 'list',
          children: {
            id: { label: 'ID', type: 'string' },
            accountNumber: { label: 'Account Number', type: 'string' },
            accountName: { label: 'Account Name', type: 'string' },
            accountType: { label: 'Account Type', type: 'string' },
            balance: { label: 'Balance', type: 'string' },
            balanceRaw: { label: 'Balance Raw', type: 'string' },
            balanceFmt: { label: 'Balance Formatted', type: 'string' },
            previousPeriodBalance: { label: 'Previous Period Balance', type: 'string' },
            previousYearBalance: { label: 'Previous Year Balance', type: 'string' },
            depth: { label: 'Depth', type: 'number' },
            isGroupHeader: { label: 'Is Group Header', type: 'boolean' },
            isSubtotal: { label: 'Is Subtotal', type: 'boolean' },
            parentGroup: { label: 'Parent Group', type: 'string' },
            hasChildren: { label: 'Has Children', type: 'boolean' },
            isLastChild: { label: 'Is Last Child', type: 'boolean' },
            isZeroBalance: { label: 'Is Zero Balance', type: 'boolean' },
          },
        },
      },
    },
    totalAssets: { label: 'Total Assets', type: 'string' },
    totalAssetsPreviousPeriod: { label: 'Total Assets Previous Period', type: 'string' },
    totalAssetsPreviousYear: { label: 'Total Assets Previous Year', type: 'string' },

    // Liabilities
    currentLiabilities: {
      label: 'Current Liabilities',
      type: 'object',
      children: {
        accountType: { label: 'Account Type', type: 'string' },
        balance: { label: 'Balance', type: 'string' },
        balanceRaw: { label: 'Balance Raw', type: 'string' },
        balanceFmt: { label: 'Balance Formatted', type: 'string' },
        previousPeriodBalance: { label: 'Previous Period Balance', type: 'string' },
        previousYearBalance: { label: 'Previous Year Balance', type: 'string' },
        accounts: {
          label: 'Accounts',
          type: 'list',
          children: {
            id: { label: 'ID', type: 'string' },
            accountNumber: { label: 'Account Number', type: 'string' },
            accountName: { label: 'Account Name', type: 'string' },
            accountType: { label: 'Account Type', type: 'string' },
            balance: { label: 'Balance', type: 'string' },
            balanceRaw: { label: 'Balance Raw', type: 'string' },
            balanceFmt: { label: 'Balance Formatted', type: 'string' },
            previousPeriodBalance: { label: 'Previous Period Balance', type: 'string' },
            previousYearBalance: { label: 'Previous Year Balance', type: 'string' },
            depth: { label: 'Depth', type: 'number' },
            isGroupHeader: { label: 'Is Group Header', type: 'boolean' },
            isSubtotal: { label: 'Is Subtotal', type: 'boolean' },
            parentGroup: { label: 'Parent Group', type: 'string' },
            hasChildren: { label: 'Has Children', type: 'boolean' },
            isLastChild: { label: 'Is Last Child', type: 'boolean' },
            isZeroBalance: { label: 'Is Zero Balance', type: 'boolean' },
          },
        },
      },
    },
    longTermLiabilities: {
      label: 'Long Term Liabilities',
      type: 'object',
      children: {
        accountType: { label: 'Account Type', type: 'string' },
        balance: { label: 'Balance', type: 'string' },
        balanceRaw: { label: 'Balance Raw', type: 'string' },
        balanceFmt: { label: 'Balance Formatted', type: 'string' },
        previousPeriodBalance: { label: 'Previous Period Balance', type: 'string' },
        previousYearBalance: { label: 'Previous Year Balance', type: 'string' },
        accounts: {
          label: 'Accounts',
          type: 'list',
          children: {
            id: { label: 'ID', type: 'string' },
            accountNumber: { label: 'Account Number', type: 'string' },
            accountName: { label: 'Account Name', type: 'string' },
            accountType: { label: 'Account Type', type: 'string' },
            balance: { label: 'Balance', type: 'string' },
            balanceRaw: { label: 'Balance Raw', type: 'string' },
            balanceFmt: { label: 'Balance Formatted', type: 'string' },
            previousPeriodBalance: { label: 'Previous Period Balance', type: 'string' },
            previousYearBalance: { label: 'Previous Year Balance', type: 'string' },
            depth: { label: 'Depth', type: 'number' },
            isGroupHeader: { label: 'Is Group Header', type: 'boolean' },
            isSubtotal: { label: 'Is Subtotal', type: 'boolean' },
            parentGroup: { label: 'Parent Group', type: 'string' },
            hasChildren: { label: 'Has Children', type: 'boolean' },
            isLastChild: { label: 'Is Last Child', type: 'boolean' },
            isZeroBalance: { label: 'Is Zero Balance', type: 'boolean' },
          },
        },
      },
    },
    totalLiabilities: { label: 'Total Liabilities', type: 'string' },
    totalLiabilitiesPreviousPeriod: { label: 'Total Liabilities Previous Period', type: 'string' },
    totalLiabilitiesPreviousYear: { label: 'Total Liabilities Previous Year', type: 'string' },

    // Equity
    equity: {
      label: 'Equity',
      type: 'object',
      children: {
        accountType: { label: 'Account Type', type: 'string' },
        balance: { label: 'Balance', type: 'string' },
        balanceRaw: { label: 'Balance Raw', type: 'string' },
        balanceFmt: { label: 'Balance Formatted', type: 'string' },
        previousPeriodBalance: { label: 'Previous Period Balance', type: 'string' },
        previousYearBalance: { label: 'Previous Year Balance', type: 'string' },
        accounts: {
          label: 'Accounts',
          type: 'list',
          children: {
            id: { label: 'ID', type: 'string' },
            accountNumber: { label: 'Account Number', type: 'string' },
            accountName: { label: 'Account Name', type: 'string' },
            accountType: { label: 'Account Type', type: 'string' },
            balance: { label: 'Balance', type: 'string' },
            balanceRaw: { label: 'Balance Raw', type: 'string' },
            balanceFmt: { label: 'Balance Formatted', type: 'string' },
            previousPeriodBalance: { label: 'Previous Period Balance', type: 'string' },
            previousYearBalance: { label: 'Previous Year Balance', type: 'string' },
            depth: { label: 'Depth', type: 'number' },
            isGroupHeader: { label: 'Is Group Header', type: 'boolean' },
            isSubtotal: { label: 'Is Subtotal', type: 'boolean' },
            parentGroup: { label: 'Parent Group', type: 'string' },
            hasChildren: { label: 'Has Children', type: 'boolean' },
            isLastChild: { label: 'Is Last Child', type: 'boolean' },
            isZeroBalance: { label: 'Is Zero Balance', type: 'boolean' },
          },
        },
      },
    },
    netIncome: { label: 'Net Income', type: 'string' },
    netIncomePreviousPeriod: { label: 'Net Income Previous Period', type: 'string' },
    netIncomePreviousYear: { label: 'Net Income Previous Year', type: 'string' },
    totalEquity: { label: 'Total Equity', type: 'string' },
    totalEquityPreviousPeriod: { label: 'Total Equity Previous Period', type: 'string' },
    totalEquityPreviousYear: { label: 'Total Equity Previous Year', type: 'string' },

    // Totals
    totalLiabilitiesAndEquity: { label: 'Total Liabilities and Equity', type: 'string' },
    totalLiabilitiesAndEquityPreviousPeriod: { label: 'Total Liabilities and Equity Previous Period', type: 'string' },
    totalLiabilitiesAndEquityPreviousYear: { label: 'Total Liabilities and Equity Previous Year', type: 'string' },
  },
};
